import { bankTransfer } from '@assets/png/imagesPng';
import LocalImage from '@atoms/LocalImage';
import SecurityRow from '@atoms/SecurityRow';
import { Text, View } from '@components/primitives';
import { MaterialIcons } from '@expo/vector-icons';
import ScreenNames from '@navigation/navigationConstants';
import { StackActions, StackNavigationProp } from '@react-navigation/native';
import appStyles from '@root/appStyles';
import { COMMON_CONSTANTS, FEATURE_FLAGS, SECURITY_PIN_INFO_MODAL } from '@root/constants';
import colors from '@theme/colors';
import { AMPLITUDE_EVENT_SOURCES } from '@utils/analytics/constants';
import { scale } from '@utils/normalize';
import { SVG_ICONS } from '@utils/SvgIcons';
import { goBackToHome, returnBottomSheetContent, validatePin } from '@utils/utils';
import React from 'react';
import { Linking } from 'react-native';
import {
  logAddPhoneClicked,
  logBankClicked,
  logChangePhoneClicked,
  logCloseModal,
  logHelpCheckCreatePin,
  logOkCreate,
  logPrimaryPress,
  logReCreatePin,
  logResetPinClicked,
  logPinCreationFailedFinal,
  logPinSubmitted,
  logContinueButtonClicked,
  logPinCreationFailed,
  logPinInputted,
  logPinCreationInputted,
  logPinInputFailed,
  logXButtonClickedSubmitted,
  logCloseModalSource,
  logPinInputSuccessfully,
} from './analyticsCreatePiin';
import styles from './styles';
// Define interfaces for complex types
interface Route {
  params?: { type?: string; source?: string };
}

interface InfoModal {
  visible: boolean;
  data: any; // Replace with specific type if known
  errorCode: string;
}

interface FeatureAvailability {
  [key: string]: { show: boolean };
}

interface PinHandlerParams {
  setOtp: React.Dispatch<React.SetStateAction<string>>;
  setVerifyInvalid: React.Dispatch<React.SetStateAction<boolean>>;
  errTxtMsg: string;
  setErrTxtMsg: React.Dispatch<React.SetStateAction<string>>;
  setClearInputs: React.Dispatch<React.SetStateAction<boolean>>;
  route: Route;
  navigation: StackNavigationProp<any>;
  setInfoModal: React.Dispatch<React.SetStateAction<InfoModal>>;
  infoModal: InfoModal;
  setTypeScreen: React.Dispatch<React.SetStateAction<string>>;
  MODAL_KEYS: typeof MODAL_KEYS;
  onVerifyCallBack: (values: any) => void;
  onVerifyValues: any;
}

interface OtherHandlerParams {
  typeScreen: string;
  route: Route;
  verifyPinHandler: (pin: string) => void;
  verifyPinOpenHandler: (payload: { securityPin: string; token: string }) => void;
  setDisableOTP: React.Dispatch<React.SetStateAction<boolean>>;
  confirmCopy: string;
  invalidConfirms: number;
  setInfoModal: React.Dispatch<React.SetStateAction<InfoModal>>;
  setInvalidConfirms: React.Dispatch<React.SetStateAction<number>>;
  errorHandler: (errorMessage: string) => void;
  setConfirmCopy: React.Dispatch<React.SetStateAction<string>>;
  otpNew: string;
  setTypeScreen: React.Dispatch<React.SetStateAction<string>>;
  securityPinRegistration: (pin: string) => void;
  t: (key: string) => string;
  securityPinOpen: { token: string; remainingAttempts?: number; attempts?: number };
}

interface VerifyPinParams {
  securityPin: { invalidAttempts: number };
  setShowPinRegister: React.Dispatch<
    React.SetStateAction<{ showPinRegister: boolean; isActive: boolean; invalidAttempts: number }>
  >;
  setVerifyInvalid: React.Dispatch<React.SetStateAction<boolean>>;
  route: Route;
  t: (key: string) => string;
  errorHandler: (errorMessage: string) => void;
  setInfoModal: React.Dispatch<React.SetStateAction<InfoModal>>;
  securityPinVerify: (pin: string) => void;
  securityPinOpenVerify: (payload: { token: string; securityPin: string }) => void;
  securityPinOpen: { token: string; remainingAttempts?: number; attempts?: number };
  setSecurityPinOpen: React.Dispatch<
    React.SetStateAction<{ token: string; remainingAttempts?: number; attempts?: number }>
  >;
  queryClient: any; // Replace with specific type if known
}

function commonPinHandlerFunction(type: string, route: Route): void {
  if (type === 'link') {
    logHelpCheckCreatePin(route);
  }
  Linking.openURL(COMMON_CONSTANTS.support);
}

export const getTitleName = (screenType: string, t: (key: string) => string): string => {
  if (screenType === 'confirm') return t('securityPin.reEnterPin');
  if (screenType === 'create') return t('securityPin.makeNewPin');
  if (screenType === 'verifyPin' || screenType === 'verifyPinWithExpiry') return t('securityPin.enterPin');
  return '';
};

export function shouldShowErrorText(errTxtMsg: string): JSX.Element | null {
  if (errTxtMsg.length > 0) {
    return (
      <Text variant="md-normal" style={styles.errorTextStyles}>
        {errTxtMsg}
      </Text>
    );
  }
  return null;
}

export function shoulRenderInstructions(
  show: boolean,
  isFeatureAvailale: FeatureAvailability,
  t: (key: string) => string,
): JSX.Element | null {
  if (show) {
    const PhoneChangeSvg = SVG_ICONS.phoneChange.phoneChange;

    return (
      <>
        <View style={styles.instructionStyles}>
          <MaterialIcons name="info-outline" color={colors.secondary.orchid} size={24} />
          <Text variant="xsm-bold" style={appStyles.marginHorizontal(8)}>
            {t('securityPin.securityInfo')}
          </Text>
        </View>

        <SecurityRow
          title={t('login.SECURITY_PIN_ACTIVE_PHONE_ENROLL_BUTTON1')}
          featureImage={SVG_ICONS.BPIcons.phoneSelector}
          description={t('securityPin.phoneInfo')}
          show={isFeatureAvailale[FEATURE_FLAGS.PHONE_ENROLL].show}
        />
        <SecurityRow
          title={t('login.SECURITY_PIN_ACTIVE_BANK_ENROLL_BUTTON')}
          description={t('securityPin.bankInfo')}
          show={isFeatureAvailale[FEATURE_FLAGS.BANK_SEL_TRNSF].show}
          featureImage={
            <LocalImage source={bankTransfer} contentFit="contain" style={{ width: scale(32), height: scale(32) }} />
          }
        />
        <SecurityRow
          title={t('login.SECURITY_PIN_ACTIVE_PHONE_CHANGE_TEXT')}
          description={t('securityPin.phoneChangeInfo')}
          show={isFeatureAvailale[FEATURE_FLAGS.PHONE_NUMBER_CHANGE]?.show}
          featureImage={
            <View
              backgroundColor={colors.secondary.softOrchid}
              paddingX="4px"
              paddingBottom="4.5px"
              paddingTop="3.5px"
              justifyContent="center"
              borderRadius="12px">
              <PhoneChangeSvg />
            </View>
          }
        />
      </>
    );
  }

  return null;
}

export function pinHandlerFunctions(params: PinHandlerParams) {
  const {
    setOtp,
    setVerifyInvalid,
    errTxtMsg,
    setErrTxtMsg,
    setClearInputs,
    route,
    navigation,
    setInfoModal,
    infoModal,
    setTypeScreen,
    MODAL_KEYS,
    onVerifyCallBack,
    onVerifyValues,
  } = params;

  const onCodeChangedHandler = (code: string): void => {
    setOtp(code);
    setVerifyInvalid(false);
    if (errTxtMsg.length > 0) setErrTxtMsg('');
    setClearInputs(false);
  };

  const resetPinHandler = (type: string): void => {
    commonPinHandlerFunction(type, route);
  };

  const errorHandler = (errorMessage: string): void => {
    setErrTxtMsg(errorMessage);
    setOtp('');
    setClearInputs(true);
  };

  const markVerifySuccess = (/* param: string */): void => {
    navigation.goBack();
  };

  const onCloseModal = (): void => {
    setInfoModal(prevState => ({ ...prevState, visible: false }));
    if (infoModal.errorCode === 'THREE_RETRY_VERIFICATION') {
      logCloseModal(route);
      return markVerifySuccess(/* 'failed' */);
    }
    if (infoModal.errorCode === 'THREE_RETRY') {
      setTypeScreen('create');
      errorHandler('');
      logCloseModalSource(route);
      return;
    }

    if (infoModal.errorCode === 'VERIFY_PIN_LOCKED') {
      goBackToHome(navigation);
    }

    logXButtonClickedSubmitted(route);
    goBackToHome(navigation);
  };

  const onPrimaryPress = (): void => {
    setInfoModal(prevState => ({ ...prevState, visible: false }));
    if (infoModal.errorCode === 'THREE_RETRY_VERIFICATION') {
      logPrimaryPress(route);
      markVerifySuccess(/* 'failed' */);
      onVerifyCallBack({ ...onVerifyValues, securityPin: null });
    }

    if (infoModal.errorCode === 'THREE_RETRY') {
      setTypeScreen('create');
      errorHandler('');
      logReCreatePin(route);
    }

    if (infoModal.errorCode === 'VERIFY_PIN_LOCKED') {
      goBackToHome(navigation);
    }

    if (infoModal.errorCode === MODAL_KEYS.GG_01_FALLBACK) {
      logOkCreate(route);
      goBackToHome(navigation);
    }

    if (infoModal.errorCode === MODAL_KEYS.GG_01) {
      const source = AMPLITUDE_EVENT_SOURCES.pin_modal_others_page;
      logAddPhoneClicked(source);
      navigation.dispatch(StackActions.replace(ScreenNames.GG_ADD_PHONE_SCREEN, { source }));
    }

    if (infoModal.errorCode === MODAL_KEYS.GG_01_PHONE_ENROLL) {
      const source = AMPLITUDE_EVENT_SOURCES.pin_modal_others_page;
      logAddPhoneClicked(source);
      navigation.dispatch(StackActions.replace(ScreenNames.GG_ADD_PHONE_SCREEN, { source }));
    }

    if (infoModal.errorCode === MODAL_KEYS.GG_01_BANK_ENROLL) {
      const source = AMPLITUDE_EVENT_SOURCES.pin_modal_others_page;
      logBankClicked(source);
      navigation.dispatch(StackActions.replace(ScreenNames.GG_ADD_BANK_SCREEN, { source }));
    }

    if (infoModal.errorCode === MODAL_KEYS.SECURITY_PIN_ENROLLED_PHONE_CHANGE) {
      const source = AMPLITUDE_EVENT_SOURCES.add_phonenumber_page;
      logChangePhoneClicked(source);
      navigation.dispatch(StackActions.replace(ScreenNames.GG_PHONE_NUMBER_CHANGE, { source }));
    }
  };

  const onPressSecondaryHandler = (): void => {
    setInfoModal(prevState => ({ ...prevState, visible: false }));

    if (infoModal.errorCode === MODAL_KEYS.GG_01) {
      const source = AMPLITUDE_EVENT_SOURCES.pin_modal_others_page;
      logBankClicked(source);
      navigation.dispatch(
        StackActions.replace(ScreenNames.GG_ADD_BANK_SCREEN, {
          pop: false,
          source,
        }),
      );
    }

    if (infoModal.errorCode === 'THREE_RETRY_VERIFICATION') {
      resetPinHandler('link');
      logResetPinClicked(route);
    }
  };

  return {
    errorHandler,
    markVerifySuccess,
    onCodeChangedHandler,
    onPrimaryPress,
    onPressSecondaryHandler,
    onCloseModal,
  };
}

export function otherPinHandlers(otherHandlerParams: OtherHandlerParams) {
  const {
    typeScreen,
    route,
    verifyPinHandler,
    verifyPinOpenHandler,
    setDisableOTP,
    confirmCopy,
    invalidConfirms,
    setInfoModal,
    setInvalidConfirms,
    errorHandler,
    setConfirmCopy,
    otpNew,
    setTypeScreen,
    securityPinRegistration,
    t,
    securityPinOpen,
  } = otherHandlerParams;

  const getPageNumber = (): number | null => {
    if (typeScreen === 'create') return 1;
    if (typeScreen === 'confirm') return 2;
    return null;
  };

  const onCodeFilledHandler = (code: string): void => {
    if (route.params?.type === 'create') {
      logPinCreationInputted(getPageNumber, route);
    }

    if (typeScreen === 'verifyPin') {
      verifyPinHandler(code);
      logPinInputted(route);
      return;
    }

    if (typeScreen === 'verifyPinWithExpiry') {
      verifyPinOpenHandler({ securityPin: code, token: securityPinOpen.token });
      return;
    }

    if (validatePin(code)) {
      setDisableOTP(false);
      if (typeScreen === 'confirm' && code !== confirmCopy) {
        setDisableOTP(true);
        logPinCreationFailed(route, getPageNumber, invalidConfirms);

        if (invalidConfirms === 2) {
          setInfoModal({
            visible: true,
            data: returnBottomSheetContent(SECURITY_PIN_INFO_MODAL.THREE_RETRY, SECURITY_PIN_INFO_MODAL.THREE_RETRY),
            errorCode: 'THREE_RETRY',
          });
          return;
        }

        setInvalidConfirms(prevState => prevState + 1);

        setTimeout(() => {
          errorHandler(t('securityPin.errorPinNotSame'));
        }, 0);
      }
    } else {
      logPinCreationFailedFinal(route, getPageNumber);
      setDisableOTP(true);
      setTimeout(() => {
        errorHandler(t('securityPin.errorPinFormat'));
      }, 0);
    }
  };

  const onNextPress = async (): Promise<void> => {
    if (typeScreen === 'create') {
      logContinueButtonClicked(route);
      setConfirmCopy(otpNew);
      setTypeScreen('confirm');
      errorHandler('');
      return;
    }
    if (typeScreen === 'confirm') {
      logPinSubmitted(route);
    }
    securityPinRegistration(otpNew);
  };

  return { getPageNumber, onNextPress, onCodeFilledHandler };
}

export function shouldRenderOnVerifyPinSuccess(
  isSuccessVerify: boolean,
  dataVerify: any,
  route: Route,
  onVerifyCallBack: (values: any) => void,
  onVerifyValues: any,
  otpEntered: string,
  setShowPinRegister: React.Dispatch<
    React.SetStateAction<{ showPinRegister: boolean; isActive: boolean; invalidAttempts: number }>
  >,
): void {
  if (isSuccessVerify && dataVerify) {
    setShowPinRegister({
      showPinRegister: false,
      isActive: true,
      invalidAttempts: 0,
    });
    logPinInputSuccessfully(route);
    onVerifyCallBack({ ...onVerifyValues, securityPin: otpEntered });
  }
}

export function shouldRenderOnVerifyOpenSuccess(
  isSuccessVerify: boolean,
  dataVerify: any,
  route: Route,
  onVerifyCallBack: () => void,
  setShowPinRegister: React.Dispatch<
    React.SetStateAction<{ showPinRegister: boolean; isActive: boolean; invalidAttempts: number }>
  >,
): void {
  if (isSuccessVerify && dataVerify) {
    setShowPinRegister({
      showPinRegister: false,
      isActive: true,
      invalidAttempts: 0,
    });
    logPinInputSuccessfully(route);
    if (onVerifyCallBack) {
      onVerifyCallBack();
    }
  }
}

export function shouldRenderOnOpenPinError(
  isErrorOpen: boolean,
  errorOpen: any,
  openPinErrorHandling: (error: any) => void,
): void {
  if (isErrorOpen && errorOpen) {
    openPinErrorHandling(errorOpen);
  }
}

export function shouldRenderOnVerifyPinError(
  isErrorVerify: boolean,
  errorVerify: any,
  verifyPinErrorHandling: (error: any) => void,
): void {
  if (isErrorVerify && errorVerify) {
    verifyPinErrorHandling(errorVerify);
  }
}

export function shouldRenderSuccessPinCreation(
  isSuccess: boolean,
  data: any,
  setInfoModal: React.Dispatch<React.SetStateAction<InfoModal>>,
  getModalNameKey: string,
  setShowPinRegister: React.Dispatch<
    React.SetStateAction<{ showPinRegister: boolean; isActive: boolean; invalidAttempts: number }>
  >,
  sourceParam: string | null,
  queryPinOpen: { refetch: () => void },
): void {
  if (isSuccess && data) {
    setInfoModal({
      visible: true,
      data: returnBottomSheetContent(
        SECURITY_PIN_INFO_MODAL[getModalNameKey],
        SECURITY_PIN_INFO_MODAL[getModalNameKey],
      ),
      errorCode: getModalNameKey,
    });
    setShowPinRegister({
      showPinRegister: false,
      isActive: true,
      invalidAttempts: 0,
    });
    if (sourceParam === 'phone_change') {
      queryPinOpen.refetch();
    }
  }
}

export function phoneChangeEnrolledPinStart(
  securityPin: string,
  securityPinToken: string,
  securityPinOpenVerify: (payload: { token: string; securityPin: string }) => void,
): void {
  securityPinOpenVerify({
    token: securityPinToken,
    securityPin,
  });
}

export function verifyAndResetPinHandlers(verifyPinParams: VerifyPinParams) {
  const {
    securityPin,
    setShowPinRegister,
    setVerifyInvalid,
    route,
    t,
    errorHandler,
    setInfoModal,
    securityPinVerify,
    securityPinOpenVerify,
    securityPinOpen,
    setSecurityPinOpen,
    queryClient,
  } = verifyPinParams;

  const verifyPinErrorHandling = (): void => {
    const remainingAttempts = 3 - (securityPin.invalidAttempts + 1);

    setShowPinRegister({ ...securityPin, invalidAttempts: securityPin.invalidAttempts + 1 });

    setVerifyInvalid(true);

    logPinInputFailed(route, securityPin);

    if (remainingAttempts > 0) {
      const errorPart1 = t('securityPin.retry1');
      const errorPart2 = t('securityPin.retry2');
      return errorHandler(`${errorPart1} ${remainingAttempts} ${errorPart2}`);
    }

    return setInfoModal({
      visible: true,
      data: returnBottomSheetContent(
        SECURITY_PIN_INFO_MODAL.THREE_RETRY_VERIFICATION,
        SECURITY_PIN_INFO_MODAL.THREE_RETRY_VERIFICATION,
      ),
      errorCode: 'THREE_RETRY_VERIFICATION',
    });
  };

  const verifyPinOpenErrorHandling = (): void => {
    setVerifyInvalid(true);
    const remainingAttempts = (securityPinOpen.remainingAttempts || 0) - 1;
    const attempts = (securityPinOpen.attempts || 0) + 1;
    setSecurityPinOpen({ ...securityPinOpen, remainingAttempts, attempts });
    if (remainingAttempts > 0) {
      const errorPart1 = t('securityPin.retry1');
      const errorPart2 = t('securityPin.retry2');
      return errorHandler(`${errorPart1} ${remainingAttempts} ${errorPart2}`);
    }

    queryClient.removeQueries({ queryKey: ['pin_security_open'] });

    return setInfoModal({
      visible: true,
      data: returnBottomSheetContent(
        SECURITY_PIN_INFO_MODAL.VERIFY_PIN_LOCKED,
        SECURITY_PIN_INFO_MODAL.VERIFY_PIN_LOCKED,
      ),
      errorCode: 'VERIFY_PIN_LOCKED',
    });
  };

  const verifyPinHandler = async (pin: string): Promise<void> => {
    securityPinVerify(pin);
  };

  const verifyPinOpenHandler = async (payload: { securityPin: string; token: string }): Promise<void> => {
    securityPinOpenVerify(payload);
  };

  const resetPinHandler = (type: string): void => {
    commonPinHandlerFunction(type, route);
  };

  return {
    verifyPinHandler,
    verifyPinErrorHandling,
    resetPinHandler,
    verifyPinOpenHandler,
    verifyPinOpenErrorHandling,
  };
}

export const MODAL_KEYS = {
  GG_01: 'GG_01',
  GG_01_FALLBACK: 'GG_01_FALLBACK',
  GG_01_PHONE_ENROLL: 'GG_01_PHONE_ENROLL',
  GG_01_BANK_ENROLL: 'GG_01_BANK_ENROLL',
  SECURITY_PIN_ENROLLED_PHONE_CHANGE: 'SECURITY_PIN_ENROLLED_PHONE_CHANGE',
} as const;

export function getModalKeyBasedOnFeatureFlags(isFeatureAvailale: FeatureAvailability, source: string | null = null) {
  return (): string => {
    if (source === 'phone_change' && isFeatureAvailale[FEATURE_FLAGS.PHONE_NUMBER_CHANGE].show) {
      return MODAL_KEYS.SECURITY_PIN_ENROLLED_PHONE_CHANGE;
    }
    if (isFeatureAvailale[FEATURE_FLAGS.PHONE_ENROLL].show && isFeatureAvailale[FEATURE_FLAGS.BANK_SEL_TRNSF].show) {
      return MODAL_KEYS.GG_01;
    }
    if (isFeatureAvailale[FEATURE_FLAGS.PHONE_ENROLL].show) {
      return MODAL_KEYS.GG_01_PHONE_ENROLL;
    }
    if (isFeatureAvailale[FEATURE_FLAGS.BANK_SEL_TRNSF].show) {
      return MODAL_KEYS.GG_01_BANK_ENROLL;
    }
    return MODAL_KEYS.GG_01_FALLBACK;
  };
}
